import { Box, Section } from '@cmg/design-system';
import { useFormikContext } from 'formik';
import React, { useCallback } from 'react';

import type { XcEmailSubscriptions } from '../../../../../types/domain/self/profile';
import { ConfigurationSwitch } from './ConfigurationSwitch';
import { XcEmailReadRow } from './XcEmailReadRow';

type NotificationItem = {
  notificationType: string;
  label: string;
  description: string;
};

type NotificationsByCategory = Readonly<Record<string, readonly NotificationItem[]>>;

type Props = {
  categories: string[];
  notificationsByCategory: NotificationsByCategory;
  isEditing: boolean;
  disabled?: boolean;
};

export const XcEmailCategorySections: React.FC<Props> = ({
  categories,
  notificationsByCategory,
  isEditing,
  disabled = false,
}) => {
  const { values, setFieldValue } = useFormikContext<XcEmailSubscriptions>();

  const onToggle = useCallback(
    (notificationType: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
      void setFieldValue(notificationType, e.target.checked);
    },
    [setFieldValue]
  );

  return (
    <Box display="flex" flexDirection="column" gap={2}>
      {categories.map(category => {
        const notifications = notificationsByCategory[category];
        if (!notifications) return null;

        return (
          <Section key={category} title={category} variant="subsection">
            <Box display="flex" flexDirection="column" gap={2}>
              {notifications.map(({ notificationType, label, description }) => {
                const checked = Boolean(
                  (values as Record<string, boolean | undefined>)[notificationType]
                );

                return isEditing ? (
                  <ConfigurationSwitch
                    key={notificationType}
                    label={label}
                    description={description}
                    checked={checked}
                    disabled={disabled}
                    onChange={onToggle(notificationType)}
                  />
                ) : (
                  <XcEmailReadRow
                    key={notificationType}
                    label={label}
                    description={description}
                    isEnabled={checked}
                  />
                );
              })}
            </Box>
          </Section>
        );
      })}
    </Box>
  );
};
